import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, Circle, ArrowRight, BookOpen, ChevronDown, ChevronUp } from "lucide-react";

export default function GuidePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await api.get("/guide/steps");
        setData(d);
        // Auto-expand first incomplete step
        const first = d.steps.find(s => !s.completed);
        if (first) setExpanded(first.id);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const completeStep = async (stepId, path) => {
    try {
      await api.post("/guide/complete-step", { step_id: stepId });
      // Refresh
      const { data: d } = await api.get("/guide/steps");
      setData(d);
      if (path) navigate(path);
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="stratum-card p-12 text-center text-[#6B7280]">Loading guide...</div>;
  if (!data) return null;

  const pct = Math.round((data.completed / data.total) * 100);
  const categories = [...new Set(data.steps.map(s => s.category))];

  return (
    <div className="space-y-6" data-testid="guide-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <BookOpen size={24} className="inline mr-2 text-[#C8A961]" />
            How to Use Stratum
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Step-by-step guided walkthrough of every feature</p>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-semibold text-[#C8A961] tabular-nums">{pct}%</div>
          <div className="text-xs text-[#6B7280]">{data.completed} of {data.total} complete</div>
        </div>
      </div>

      {/* Progress */}
      <div className="stratum-card p-4">
        <div className="h-3 bg-[#E8E2D5] rounded-full overflow-hidden">
          <div className="h-full bg-[#C8A961] rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Steps grouped by category */}
      {categories.map(cat => (
        <div key={cat}>
          <h3 className="text-xs uppercase tracking-[0.2em] text-[#C8A961] font-medium mb-3 mt-6">{cat}</h3>
          <div className="space-y-2">
            {data.steps.filter(s => s.category === cat).map(step => (
              <div key={step.id} className={`stratum-card overflow-hidden ${step.completed ? "border-l-4 border-l-[#065F46]" : ""}`} data-testid={`guide-step-${step.id}`}>
                <button onClick={() => setExpanded(expanded === step.id ? null : step.id)}
                  className="w-full text-left p-4 flex items-center gap-4 hover:bg-[#FAF8F4] transition-colors">
                  {step.completed
                    ? <CheckCircle size={20} className="text-[#065F46] flex-shrink-0" />
                    : <Circle size={20} className="text-[#E8E2D5] flex-shrink-0" />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-[#C8A961]">{String(step.order).padStart(2, "0")}</span>
                      <span className="text-sm font-medium text-[#0B1020]">{step.title}</span>
                    </div>
                    <p className="text-xs text-[#6B7280] mt-0.5">{step.description}</p>
                  </div>
                  {expanded === step.id ? <ChevronUp size={16} className="text-[#6B7280]" /> : <ChevronDown size={16} className="text-[#6B7280]" />}
                </button>
                {expanded === step.id && (
                  <div className="px-4 pb-4 border-t border-[#E8E2D5] pt-4 bg-[#FAF8F4]/50">
                    <p className="text-sm text-[#1A1A1A] leading-relaxed mb-4">{step.content}</p>
                    <div className="flex gap-3">
                      <button onClick={() => completeStep(step.id, step.path)}
                        className="stratum-btn-primary flex items-center gap-2 text-sm" data-testid={`guide-action-${step.id}`}>
                        {step.action} <ArrowRight size={14} />
                      </button>
                      {!step.completed && (
                        <button onClick={() => completeStep(step.id)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm text-[#6B7280] hover:bg-white">
                          Mark as read
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
